import { createApiCall } from './index'

/**
 * VERA-803 — Field mapping client.
 * Maps integration (e.g. Shopify) source fields to Vera's unified-schema fields.
 * Types mirror the `@repo/types` field-mapping contract (platform has no
 * @repo/types dep, so they are re-declared locally like shopifyApi.ts).
 */
export type FieldMappingObject = 'product' | 'owner' | 'tag'

export interface FieldMapping {
  id?: string
  company_id?: string
  connection_id: string
  object: FieldMappingObject
  vera_field: string
  source_field: string
  bidirectional: boolean
}

export interface VeraFieldDescriptor {
  object: FieldMappingObject
  field: string
  label: string
  required?: boolean
}

export interface SourceFieldDescriptor {
  key: string
  label: string
  group?: string
  /** False when the import pipeline can't consume this field yet (UI greys it out). */
  supported?: boolean
  /** VRG-100 — which integration this source field comes from (unified catalog only). */
  provider?: string
}

export interface FieldMappingConfig {
  connection_id: string
  bidirectional: boolean
  /**
   * VRG-103 — the connection's global "Sync value updates" setting
   * (ecommerce_connections.sync_value_updates_enabled). Distinct from
   * `bidirectional`, which is the opposite direction (Vera→source writeback,
   * still inert).
   */
  sync_value_updates_enabled: boolean
  mappings: FieldMapping[]
}

export interface FieldMappingCatalog {
  veraFields: VeraFieldDescriptor[]
  sourceFields: SourceFieldDescriptor[]
}

export interface SaveFieldMappingsPayload {
  bidirectional: boolean
  /** VRG-103 — see FieldMappingConfig.sync_value_updates_enabled. */
  sync_value_updates_enabled: boolean
  mappings: Array<{
    object: FieldMappingObject
    vera_field: string
    source_field: string
  }>
}

// VRG-100 — unified cross-integration shapes.
export interface FieldMappingIntegration {
  provider: string
  connection_id: string
}

export interface UnifiedFieldMappingConfig {
  integrations: FieldMappingIntegration[]
  mappings: FieldMapping[]
  bidirectional: boolean
  /** VRG-103 — see FieldMappingConfig.sync_value_updates_enabled. */
  sync_value_updates_enabled: boolean
}

export interface UnifiedFieldMappingCatalog {
  veraFields: VeraFieldDescriptor[]
  sourceFields: SourceFieldDescriptor[]
  integrations: FieldMappingIntegration[]
}

export interface SaveUnifiedFieldMappingsPayload {
  bidirectional: boolean
  /** VRG-103 — applies to every touched connection, same as `bidirectional`. */
  sync_value_updates_enabled: boolean
  mappings: Array<{
    provider: string
    object: FieldMappingObject
    vera_field: string
    source_field: string
  }>
}

const base = (provider: string) => `/integrations/field-mapping/${provider}`
const unifiedBase = '/integrations/field-mapping/unified'

export const fieldMappingApi = {
  async getConfig(provider: string): Promise<FieldMappingConfig> {
    const response = await createApiCall({ method: 'GET', url: base(provider) })
    if (response.status !== 200) {
      throw new Error(response.data?.message || 'Failed to load field mapping')
    }
    return response.data
  },

  async getCatalog(provider: string): Promise<FieldMappingCatalog> {
    const response = await createApiCall({ method: 'GET', url: `${base(provider)}/catalog` })
    if (response.status !== 200) {
      throw new Error(response.data?.message || 'Failed to load field catalog')
    }
    return response.data
  },

  async saveConfig(
    provider: string,
    payload: SaveFieldMappingsPayload,
  ): Promise<FieldMappingConfig> {
    const response = await createApiCall({ method: 'PUT', url: base(provider), data: payload })
    if (response.status !== 200) {
      throw new Error(response.data?.message || 'Failed to save field mapping')
    }
    return response.data
  },

  // ---- VRG-100 — unified cross-integration ----

  async getUnifiedConfig(): Promise<UnifiedFieldMappingConfig> {
    const response = await createApiCall({ method: 'GET', url: unifiedBase })
    if (response.status !== 200) {
      throw new Error(response.data?.message || 'Failed to load field mapping')
    }
    return response.data
  },

  async getUnifiedCatalog(): Promise<UnifiedFieldMappingCatalog> {
    const response = await createApiCall({ method: 'GET', url: `${unifiedBase}/catalog` })
    if (response.status !== 200) {
      throw new Error(response.data?.message || 'Failed to load field catalog')
    }
    return response.data
  },

  async saveUnified(
    payload: SaveUnifiedFieldMappingsPayload,
  ): Promise<UnifiedFieldMappingConfig> {
    const response = await createApiCall({ method: 'PUT', url: unifiedBase, data: payload })
    if (response.status !== 200) {
      throw new Error(response.data?.message || 'Failed to save field mapping')
    }
    return response.data
  },
}
